import axios from 'axios';

const api = axios.create({
  // In production, VITE_API_URL can be set at build time (e.g. https://yourdomain.com/api).
  // Defaults to '/api' so Nginx can reverse-proxy the request to the backend.
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 600000,
});

// Attach JWT on every request if present in localStorage
api.interceptors.request.use(config => {
  const token = localStorage.getItem('tsela_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Auth ──────────────────────────────────────────────────────────────────────
export const loginUser = (username, password) =>
  api.post('/auth/login', null, { params: { username, password } });
export const getMe = () => api.get('/auth/me');
export const updateMe = (data) => api.put('/auth/me', data);

// ── User Management (admin only) ──────────────────────────────────────────────
export const listUsers       = ()           => api.get('/auth/users');
export const createUserAdmin = (data)       => api.post('/auth/users', data);
export const updateUserAdmin = (id, data)   => api.put(`/auth/users/${id}`, data);
export const deleteUserAdmin = (id)         => api.delete(`/auth/users/${id}`);

// ── Projects ──────────────────────────────────────────────────────────────────
export const getProjects = (status) => api.get('/projects/', { params: status ? { status } : {} });
export const createProject = (data) => api.post('/projects/', data);
export const getProject = (id) => api.get(`/projects/${id}`);
export const updateProject = (id, d) => api.put(`/projects/${id}`, d);
export const deleteProject = (id) => api.delete(`/projects/${id}`);
export const setProjectStatus = (id, s) => api.patch(`/projects/${id}/status`, { status: s });
export const getQaSignoff = (id) => api.get(`/projects/${id}/qa`);
export const saveQaSignoff = (id, data) => api.put(`/projects/${id}/qa`, data);
export const getProjectMeta = () => api.get('/projects/meta');
export const getNearby = (id, r) => api.get(`/projects/${id}/nearby`, { params: r ? { radius_km: r } : {} });
export const getAssessAll = (id, r) => api.get(`/projects/${id}/assess-all`, { params: r ? { radius_km: r } : {} });
export const startAssessAll = (id, r, force) => api.post(`/projects/${id}/assess-all`, {}, { params: { radius_km: r || 55.6, force: force || false } });
export const getAssessStatus = (id, jobId) => api.get(`/projects/${id}/assess-status/${jobId}`);
export const getAssessResult = (id, jobId) => api.get(`/projects/${id}/assess-result/${jobId}`);
export const getAssessCacheInfo = (id) => api.get(`/projects/${id}/assess-cache-info`);
export const uploadRunwayProfile = (id, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/projects/${id}/runway-profile`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getRunwayProfile = (id) => api.get(`/projects/${id}/runway-profile`);
export const getOlsReportData = (id) => api.get(`/projects/${id}/ols-report-data`);

export const getObstacles = () => api.get('/projects/obstacles/all');
export const createObstacle = (data) => api.post('/projects/obstacles/', data);
export const updateObstacle = (id, data) => api.patch(`/projects/obstacles/${id}`, data);
export const updateAerodromeByIcao = (icao, data) => api.patch(`/data/arp/by-icao/${icao}`, data);
export const deleteObstacle = (id) => api.delete(`/projects/obstacles/${id}`);
export const bulkDeleteObstacles = (projectId, payload) => api.post(`/projects/${projectId}/obstacles/bulk-delete`, payload);

export const uploadObstaclesCsv = (projectId, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/projects/${projectId}/obstacles/csv`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const saveAssessment = (data) => api.post('/projects/assessments/', data);

// ── Data Manager ──────────────────────────────────────────────────────────────
export const getMapData = () => api.get('/data/map-data');
export const getArpData = (search, page) => api.get('/data/arp', { params: { search, page } });
export const getCnsData = (search, page) => api.get('/data/cns', { params: { search, page } });
export const getThrData = (search, page) => api.get('/data/thr', { params: { search, page } });
export const getRwyData = (search, page) => api.get('/data/rwy', { params: { search, page } });
export const getRwyProfileData = (search, page) => api.get('/data/rwy-profile', { params: { search, page } });
export const getAirportsData = (search, page) => api.get('/data/airports', { params: { search, page } });
export const getOlsParameters = () => api.get('/data/ols-parameters');
export const getOlsLegacyByAerodrome = (icao) => api.get(`/data/ols-legacy-by-aerodrome/${icao}`);
export const getObstaclesMeta = () => api.get('/data/obstacles-meta');
export const getObstaclesSwot = (search, page) => api.get('/data/obstacles-swot', { params: { search, page } });
export const getObstaclesSof = (search, page) => api.get('/data/obstacles-sof', { params: { search, page } });
export const uploadObstaclesSwot = (fd) => api.post('/data/obstacles-swot/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const uploadObstaclesSof = (fd) => api.post('/data/obstacles-sof/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getObstaclesSwotBlocks = () => api.get('/data/obstacles-swot/blocks');
export const getObstaclesSwotBlock = (blockId) => api.get(`/data/obstacles-swot/block/${encodeURIComponent(blockId)}`);
export const getObstaclesSofBlocks = () => api.get('/data/obstacles-sof/blocks');
export const getObstaclesSofBlock = (blockId) => api.get(`/data/obstacles-sof/block/${encodeURIComponent(blockId)}`);
export const getObstaclesInHull = (points, radiusM) => api.post('/data/obstacles-in-hull', { points, radius_m: radiusM });
export const createArp = (data) => api.post('/data/arp', data);
export const createCns = (data) => api.post('/data/cns', data);
export const createThr = (data) => api.post('/data/thr', data);
export const createRwy = (data) => api.post('/data/rwy', data);
export const createRwyProfile = (data) => api.post('/data/rwy-profile', data);
export const createAirport = (data) => api.post('/data/airports', data);
export const updateArp = (id, data) => api.patch(`/data/arp/${id}`, data);
export const updateCns = (id, data) => api.patch(`/data/cns/${id}`, data);
export const updateThr = (id, data) => api.patch(`/data/thr/${id}`, data);
export const updateRwy = (id, data) => api.patch(`/data/rwy/${id}`, data);
export const updateRwyProfile = (id, data) => api.patch(`/data/rwy-profile/${id}`, data);
export const updateAirport = (id, data) => api.patch(`/data/airports/${id}`, data);
export const deleteArp = (id) => api.delete(`/data/arp/${id}`);
export const deleteCns = (id) => api.delete(`/data/cns/${id}`);
export const deleteThr = (id) => api.delete(`/data/thr/${id}`);
export const deleteRwy = (id) => api.delete(`/data/rwy/${id}`);
export const deleteRwyProfile = (id) => api.delete(`/data/rwy-profile/${id}`);
export const deleteAirport = (id) => api.delete(`/data/airports/${id}`);
export const assessCircling = (aerodrome, obstacles) => api.post('/circling/assess', { aerodrome, obstacles });
export const getCirclingMinimaData = (search, page) => api.get('/data/circling-minima', { params: { search, page } });
export const createCirclingMinima = (data) => api.post('/data/circling-minima', data);
export const updateCirclingMinima = (icao, data) => api.patch(`/data/circling-minima/${icao}`, data);
export const deleteCirclingMinima = (icao) => api.delete(`/data/circling-minima/${icao}`);
export const getMsaMinimaData = (search, page) => api.get('/data/msa-minima', { params: { search, page } });
export const createMsaMinima = (data) => api.post('/data/msa-minima', data);
export const updateMsaMinima = (icao, data) => api.patch(`/data/msa-minima/${icao}`, data);
export const deleteMsaMinima = (icao) => api.delete(`/data/msa-minima/${icao}`);
export const getMsaSectors = () => api.get('/msa/sectors');
export const bulkUploadMsaSectors = () => api.post('/msa/bulk-upload');
export const deleteMsaSectors = (ident) => api.delete(`/msa/sectors/${ident}`);
export const getClients = (search) => api.get('/data/clients', { params: { search } });
export const createClient = (data) => api.post('/data/clients', data);
export const updateClient = (id, data) => api.patch(`/data/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/data/clients/${id}`);
export const getCoordinators = () => api.get('/data/coordinators');
export const createCoordinator = (data) => api.post('/data/coordinators', data);
export const updateCoordinator = (id, data) => api.patch(`/data/coordinators/${id}`, data);
export const deleteCoordinator = (id) => api.delete(`/data/coordinators/${id}`);


// ── ILS / OAS Data Management ────────────────────────────────────────────────
export const getIlsProcs = (search, page) => api.get('/data/ils-procs', { params: { search, page } });
export const createIlsProc = (data) => api.post('/data/ils-procs', data);
export const updateIlsProc = (id, data) => api.patch(`/data/ils-procs/${id}`, data);
export const deleteIlsProc = (id) => api.delete(`/data/ils-procs/${id}`);

export const getOasConstants = (search, page) => api.get('/data/oas-constants', { params: { search, page } });
export const createOasConstant = (data) => api.post('/data/oas-constants', data);
export const updateOasConstant = (id, data) => api.patch(`/data/oas-constants/${id}`, data);
export const deleteOasConstant = (id) => api.delete(`/data/oas-constants/${id}`);

export const getOasTemplates = (search, page) => api.get('/data/oas-templates', { params: { search, page } });
export const createOasTemplate = (data) => api.post('/data/oas-templates', data);
export const updateOasTemplate = (id, data) => api.patch(`/data/oas-templates/${id}`, data);
export const deleteOasTemplate = (id) => api.delete(`/data/oas-templates/${id}`);

export const getWaypoints = (search, page) => api.get('/data/waypoints', { params: { search, page } });
export const createWaypoint = (data) => api.post('/data/waypoints', data);
export const updateWaypoint = (id, data) => api.patch(`/data/waypoints/${id}`, data);
export const deleteWaypoint = (id) => api.delete(`/data/waypoints/${id}`);

export const getAirspaceData = (search, page) => api.get('/data/airspace', { params: { search, page } });
export const createAirspace = (data) => api.post('/data/airspace', data);
export const updateAirspace = (uuid, data) => api.patch(`/data/airspace/${uuid}`, data);
export const deleteAirspace = (uuid) => api.delete(`/data/airspace/${uuid}`);
export const uploadAirspaceKmz = (formData) => api.post('/data/airspace/upload-kmz', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const bulkUploadAirspace = () => api.post('/data/airspace/bulk-upload');
export const reparseAirspace = () => api.post('/data/airspace/reparse');

// ── Geo Borders ───────────────────────────────────────────────────────────────
export const getGeoBorders = (activeOnly = false, includeGeom = false) => api.get('/geo-borders', { params: { active_only: activeOnly, include_geometry: includeGeom } });
export const toggleGeoBorder = (id, isActive) => api.patch(`/geo-borders/${id}/toggle`, { is_active: isActive });

// ── Dashboard ─────────────────────────────────────────────────────────────────
export const getDashboard = () => api.get('/dashboard/summary');
export const getDashboardStats = (params) => api.get('/dashboard/stats', { params });
export const getDashboardClients = () => api.get('/dashboard/clients');
export const getDashboardTypes = () => api.get('/dashboard/types');

// ── Aerodromes ────────────────────────────────────────────────────────────────
export const getAerodromes = () => api.get('/aerodromes/');
export const getPansAerodromes = () => api.get('/pans/ils-aerodromes');
export const getInstrumentAerodromes = () => api.get('/pans/instrument-aerodromes');
export const getLowestMsaTaa = () => api.get('/pans/lowest-msa-taa');
export const getIlsProcedures = (icao) => api.get(`/pans/ils-procedures/${icao}`);
export const assessIls = (icao) => api.get(`/pans/ils-assess/${icao}`);

// ── OLS Surfaces ──────────────────────────────────────────────────────────────
export const getOLSSurfaces  = (icao)            => api.get(`/ols-surfaces/${icao}`);
export const assessOlsSingle = (icao, obstacles) => api.post(`/ols-surfaces/assess/${icao}`, { obstacles });
export const deleteOlsScreenshot = (icao, projectId) => api.delete(`/ols-surfaces/${icao}/screenshot`, { params: projectId ? { project_id: projectId } : {} });
export const getIhsBasisComparison = (icao) => api.get(`/ols-surfaces/${icao}/ihs-basis`);
export const getOlsRunwayParameters = (icao) => api.get(`/ols-surfaces/${icao}/parameters`);

// ── OLS Surfaces (ADG / Amendment 18) ────────────────────────────────────────
export const getOlsAdgSurfaces = (icao) => api.get(`/ols-adg-surfaces/${icao}`);
export const assessOlsAdgSingle = (icao, obstacles) => api.post(`/ols-adg-surfaces/assess/${icao}`, { obstacles });

// ── NPA ───────────────────────────────────────────────────────────────────────
export const getNpaAerodromes = () => api.get('/npa/aerodromes');
export const getNpaMinima = (icao) => api.get(`/npa/minima/${icao}`);
export const assessNpa = (aerodrome, obstacles) => api.post('/npa/assess', { aerodrome, obstacles });
export const deleteNpaScreenshot = (icao, projectId) => api.delete(`/npa/screenshot/${icao}`, { params: projectId ? { project_id: projectId } : {} });
export const saveNpaTaaResults = (icao, results, projectId) => api.post('/npa/taa-results', { icao, results, project_id: projectId });
export const saveNpaHoldResults = (icao, results, projectId) => api.post('/npa/hold-results', { icao, results, project_id: projectId });

// ── VSS ───────────────────────────────────────────────────────────────────────
export const getVssAerodromes = () => api.get('/vss/aerodromes');
export const assessVss = (aerodrome, obstacles) => api.post('/vss/assess', { aerodrome, obstacles });
export const deleteVssScreenshot = (icao, projectId) => api.delete(`/vss/screenshot/${icao}`, { params: projectId ? { project_id: projectId } : {} });

// ── Procedures (RNAV / KMZ Manager) ──────────────────────────────────────────
export const getProcedures = () => api.get('/procedures');
export const getProcLayers = (id) => api.get(`/procedures/${id}/layers`);
export const deleteProcedure = (id) => api.delete(`/procedures/${id}`);
export const bulkUploadProcs = () => api.post('/procedures/bulk-upload');
export const reparseAllProcs = () => api.post('/procedures/reparse-all');
export const uploadProcedure = (formData) => api.post('/procedures/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
});
export const getProcedureForIcao = (icao) => api.get(`/npa/procedure/${icao}`);
export const patchTaaAltitudes = (id, body) => api.patch(`/procedures/${id}/taa`, body);

// ── SID Departures (KMZ Manager) ─────────────────────────────────────────────
export const getDepartures = () => api.get('/departures');
export const getDepartureFeatures = (id) => api.get(`/departures/${id}/features`);
export const deleteDeparture = (id) => api.delete(`/departures/${id}`);
export const bulkUploadDepartures = () => api.post('/departures/bulk-upload');
export const reparseAllDepartures = () => api.post('/departures/reparse-all');
export const uploadDeparture = (formData) => api.post('/departures/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
});
export const assessProjectDepartures = (projectId) => api.get(`/departures/assess/${projectId}`);
export const saveSidScreenshot = (depId, projectId, base64Data) => api.post(`/departures/${depId}/screenshot`, { image: base64Data }, { params: { project_id: projectId } });
export const deleteSidScreenshot = (depId, projectId) => api.delete(`/departures/${depId}/screenshot`, { params: { project_id: projectId } });

// ── STAR Arrivals (KMZ Manager) ───────────────────────────────────────────────
export const getArrivals = () => api.get('/arrivals');
export const getArrivalFeatures = (id) => api.get(`/arrivals/${id}/features`);
export const deleteArrival = (id) => api.delete(`/arrivals/${id}`);
export const bulkUploadArrivals = () => api.post('/arrivals/bulk-upload');
export const reparseAllArrivals = () => api.post('/arrivals/reparse-all');
export const uploadArrival = (formData) => api.post('/arrivals/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
});
export const assessProjectArrivals = (projectId) => api.get(`/arrivals/assess/${projectId}`);
export const saveStarScreenshot = (arrId, projectId, base64Data) => api.post(`/arrivals/${arrId}/screenshot`, { image: base64Data }, { params: { project_id: projectId } });
export const deleteStarScreenshot = (arrId, projectId) => api.delete(`/arrivals/${arrId}/screenshot`, { params: { project_id: projectId } });

// ── RNAV ──────────────────────────────────────────────────────────────────────
export const saveRnavScreenshot = (projectId, icao, base64Data) => api.post('/rnav/screenshot', { image: base64Data, icao }, { params: { project_id: projectId } });
export const getRnavScreenshot = (icao, projectId) => api.get(`/rnav/screenshot/${icao}`, { params: { project_id: projectId } });
export const deleteRnavScreenshot = (icao, projectId) => api.delete(`/rnav/screenshot/${icao}`, { params: { project_id: projectId } });

export const getRnavRoutes = () => api.get('/rnav/routes');
export const getRnavRouteGeometry = (id) => api.get(`/rnav/routes/${id}/geometry`);
export const uploadRnavRoute = (formData) => api.post('/rnav/routes/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const bulkUploadRnavRoutes = () => api.post('/rnav/routes/bulk-upload');
export const reparseRnavRoutes = () => api.post('/rnav/routes/reparse');
export const clearAllRnavRoutes = () => api.delete('/rnav/routes');
export const deleteRnavRoute = (id) => api.delete(`/rnav/routes/${id}`);
export const assessRnavRoutes = (projectId) => api.get(`/rnav/assess/${projectId}`);
export const assessRnavCandidates = (obstacles) => api.post('/rnav/assess', { obstacles });

// ── Airspace Assessment ─────────────────────────────────────────────────────────
export const assessProjectAirspace = (projectId, bufferKm, operationalAltOverrideFt) => api.get(`/airspace/assess/${projectId}`, {
    params: {
        ...(bufferKm ? { buffer_km: bufferKm } : {}),
        ...(operationalAltOverrideFt != null && operationalAltOverrideFt !== '' ? { operational_alt_override_ft: operationalAltOverrideFt } : {}),
    },
});
export const getCtrMaskingAngle = (payload) => api.post('/airspace/masking-angle', payload);
export const getCtrByIcao = (icao) => api.get(`/airspace/ctr/${icao}`);
export const saveAirspaceScreenshot = (projectId, base64Data) => api.post('/airspace/screenshot', { image: base64Data }, { params: { project_id: projectId } });
export const getAirspaceScreenshot = (projectId) => api.get('/airspace/screenshot', { params: { project_id: projectId } });
export const deleteAirspaceScreenshot = (projectId) => api.delete('/airspace/screenshot', { params: { project_id: projectId } });

// ── RMAC ──────────────────────────────────────────────────────────────────────
export const getRmacList    = ()                   => api.get('/rmac/list');
export const getRmacAreas   = (icao)               => api.get(`/rmac/areas/${icao}`);
export const getRmacMinima  = (icao)               => api.get(`/rmac/minima/${icao}`);
export const bulkUploadRmac = ()                   => api.post('/rmac/bulk-upload');
export const uploadRmac     = (fd)                 => api.post('/rmac/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteRmac     = (id)                 => api.delete(`/rmac/${id}`);
export const bulkDeleteRmac = (ids)                => api.post('/rmac/bulk-delete', { ids });
export const assessRmac     = (aerodrome, obstacles) => api.post('/rmac/assess', { aerodrome, obstacles });
export const getAllRmacMinima = ()                 => api.get('/rmac/minima');
export const reparseAllRmac   = ()                 => api.post('/rmac/reparse');

export default api;
